import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, downloadFile } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useLedgerAccounts } from '../api/queries';
import { addDays, formatDate, formatGhs } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, useReport } from '../ui/kit';
import { PageHead } from './shared';

type Kind = 'pl' | 'bs' | 'cash' | 'tax' | 'gl' | 'refunds';

interface RefundRow {
  id: string;
  label: string;
  amount: number;
  reason: string;
  by: string;
  createdAt: string;
  lines: { method: string; amount: number; reference: string | null }[];
}

interface Row {
  code: string;
  name: string;
  amount: number;
}
interface IncomeStatement {
  from: string;
  to: string;
  revenue: Row[];
  expenses: Row[];
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
}
interface BalanceSheet {
  asOf: string;
  assets: Row[];
  liabilities: Row[];
  equity: Row[];
  profitToDate: number;
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  balanced: boolean;
}
interface CashBook {
  rows: { code: string; name: string; opening: number; received: number; paidOut: number; closing: number }[];
  totalOpening: number;
  totalReceived: number;
  totalPaidOut: number;
  totalClosing: number;
}
interface TaxSummary {
  rows: { name: string; collected: number; returned: number; net: number }[];
  totalNet: number;
  salesBeforeTax: number;
}
interface GeneralLedger {
  account: { code: string; name: string };
  opening: number;
  closing: number;
  totalDebit: number;
  totalCredit: number;
  rows: { date: string; memo: string; source: string; debit: number; credit: number; balance: number }[];
}

const TITLES: Record<Kind, string> = { pl: 'Profit and loss', bs: 'Balance sheet', cash: 'Cash book', tax: 'Taxes', gl: 'Ledger by account', refunds: 'Refunds' };
const firstOfMonth = (d: string) => `${d.slice(0, 8)}01`;
const lastMonth = (d: string): { from: string; to: string } => {
  const end = addDays(firstOfMonth(d), -1);
  return { from: firstOfMonth(end), to: end };
};

export function Reports() {
  const { me } = useAuth();
  const today = me?.property.today ?? '';
  const [kind, setKind] = useState<Kind>('pl');
  const [from, setFrom] = useState(firstOfMonth(today));
  const [to, setTo] = useState(today);
  const [account, setAccount] = useState('1000');
  const accounts = useLedgerAccounts();
  const report = useReport();
  const [busy, setBusy] = useState(false);

  const validRange = kind === 'refunds' || from <= to;
  const path = ((): string => {
    switch (kind) {
      case 'pl':
        return `/reports/financial/income-statement?from=${from}&to=${to}`;
      case 'bs':
        return `/reports/financial/balance-sheet?asOf=${to}`;
      case 'cash':
        return `/reports/financial/cash-book?from=${from}&to=${to}`;
      case 'tax':
        return `/reports/financial/tax-summary?from=${from}&to=${to}`;
      case 'gl':
        return `/reports/financial/general-ledger?account=${account}&from=${from}&to=${to}`;
      case 'refunds':
        return '/refunds?limit=100';
    }
  })();
  const q = useQuery({ queryKey: ['fin', path], enabled: validRange, queryFn: () => api<unknown>(path) });
  const csvName = `${kind === 'bs' ? 'balance-sheet' : TITLES[kind].toLowerCase().replace(/ /g, '-')}-${kind === 'bs' ? to : `${from}-to-${to}`}.csv`;

  async function download() {
    setBusy(true);
    try {
      await downloadFile(`${path}&format=csv`, csvName);
    } catch (e) {
      report(e);
    } finally {
      setBusy(false);
    }
  }

  const setRange = (f: string, t: string) => {
    setFrom(f);
    setTo(t);
  };

  return (
    <div className="page">
      <PageHead eyebrow="Accounts" title="Reports">
        <Button loading={busy} disabled={!validRange || !q.data || kind === 'refunds'} onClick={() => void download()}>
          Download CSV
        </Button>
      </PageHead>

      <div className="seg" role="group" aria-label="Report" style={{ overflowX: 'auto' }}>
        {(Object.keys(TITLES) as Kind[]).map((k) => (
          <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>
            {TITLES[k]}
          </button>
        ))}
      </div>

      <div className="stack" hidden={kind === 'refunds'}>
        <div className="grid-2">
          {kind !== 'bs' ? (
            <Field id="rp-from" label="From">
              <input id="rp-from" className="input" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </Field>
          ) : null}
          <Field id="rp-to" label={kind === 'bs' ? 'As at' : 'To'} error={validRange ? undefined : 'The start is after the end'}>
            <input id="rp-to" className="input" type="date" value={to} min={kind === 'bs' ? undefined : from} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
        {kind !== 'bs' ? (
          <div className="row">
            <Button variant="ghost" onClick={() => setRange(today, today)}>Today</Button>
            <Button variant="ghost" onClick={() => setRange(addDays(today, -6), today)}>Last 7 days</Button>
            <Button variant="ghost" onClick={() => setRange(firstOfMonth(today), today)}>This month</Button>
            <Button variant="ghost" onClick={() => { const m = lastMonth(today); setRange(m.from, m.to); }}>Last month</Button>
          </div>
        ) : null}
        {kind === 'gl' ? (
          <Field id="rp-acct" label="Account">
            <select id="rp-acct" className="select" value={account} onChange={(e) => setAccount(e.target.value)}>
              {(accounts.data ?? []).map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} · {a.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>

      {!validRange ? null : q.isLoading ? (
        <Loading rows={4} />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : kind === 'pl' ? (
        <PL d={q.data as IncomeStatement} />
      ) : kind === 'bs' ? (
        <BS d={q.data as BalanceSheet} />
      ) : kind === 'cash' ? (
        <Cash d={q.data as CashBook} />
      ) : kind === 'tax' ? (
        <Tax d={q.data as TaxSummary} />
      ) : kind === 'gl' ? (
        <GL d={q.data as GeneralLedger} />
      ) : (
        <RefundList rows={(q.data as { refunds: RefundRow[] }).refunds} />
      )}
    </div>
  );
}

function Section({ title, rows, total, totalLabel }: { title: string; rows: Row[]; total: number; totalLabel: string }) {
  return (
    <>
      <tr className="section">
        <th colSpan={2}>{title}</th>
      </tr>
      {rows.length === 0 ? (
        <tr>
          <td colSpan={2} className="muted">Nothing recorded</td>
        </tr>
      ) : (
        rows.map((r) => (
          <tr key={r.code || r.name}>
            <td>{r.code ? `${r.code} · ` : ''}{r.name}</td>
            <td className="r num">{formatGhs(r.amount)}</td>
          </tr>
        ))
      )}
      <tr className="strong">
        <td>{totalLabel}</td>
        <td className="r num">{formatGhs(total)}</td>
      </tr>
    </>
  );
}

function PL({ d }: { d: IncomeStatement }) {
  const empty = d.revenue.length === 0 && d.expenses.length === 0;
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h2">Profit and loss</h2>
        <span className="small muted">{formatDate(d.from, true)} to {formatDate(d.to, true)}</span>
      </div>
      {empty ? (
        <Empty icon="inbox" title="No trading in this period" />
      ) : (
        <table className="ledger fin">
          <tbody>
            <Section title="Revenue (before tax)" rows={d.revenue} total={d.totalRevenue} totalLabel="Total revenue" />
            <Section title="Expenses" rows={d.expenses} total={d.totalExpenses} totalLabel="Total expenses" />
            <tr className="strong total">
              <td>{d.netProfit >= 0 ? 'Net profit' : 'Net loss'}</td>
              <td className="r num">{formatGhs(d.netProfit)}</td>
            </tr>
          </tbody>
        </table>
      )}
      <p className="hint">Revenue excludes VAT and levies, which belong to the tax authority. Expenses appear once they are recorded as journal entries; payroll and purchasing will post here automatically later.</p>
    </section>
  );
}

function BS({ d }: { d: BalanceSheet }) {
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h2">Balance sheet</h2>
        <Badge tone={d.balanced ? 'good' : 'bad'}>{d.balanced ? 'Balances' : 'Does not balance'}</Badge>
      </div>
      <p className="small muted">As at {formatDate(d.asOf, true)}</p>
      <table className="ledger fin">
        <tbody>
          <Section title="What the hotel has (assets)" rows={d.assets} total={d.totalAssets} totalLabel="Total assets" />
          <Section title="What it owes (liabilities)" rows={d.liabilities} total={d.totalLiabilities} totalLabel="Total liabilities" />
          <Section title="Owners’ share (equity)" rows={[...d.equity, { code: '', name: 'Profit to date (not yet closed to equity)', amount: d.profitToDate }]} total={d.totalEquity} totalLabel="Total equity" />
        </tbody>
      </table>
      {!d.balanced ? <div className="banner bad" role="alert">The statement does not balance. Do not use it; check the Accounts page.</div> : null}
    </section>
  );
}

function Cash({ d }: { d: CashBook }) {
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h2">Cash book</h2>
      </div>
      <div className="table-scroll">
        <table className="ledger fin">
          <thead>
            <tr>
              <th>Account</th>
              <th className="r">Opening</th>
              <th className="r">Received</th>
              <th className="r">Paid out</th>
              <th className="r">Closing</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.code}>
                <td>{r.name}</td>
                <td className="r num">{formatGhs(r.opening)}</td>
                <td className="r num">{formatGhs(r.received)}</td>
                <td className="r num">{formatGhs(r.paidOut)}</td>
                <td className="r num">{formatGhs(r.closing)}</td>
              </tr>
            ))}
            <tr className="strong">
              <td>Total</td>
              <td className="r num">{formatGhs(d.totalOpening)}</td>
              <td className="r num">{formatGhs(d.totalReceived)}</td>
              <td className="r num">{formatGhs(d.totalPaidOut)}</td>
              <td className="r num">{formatGhs(d.totalClosing)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="hint">Mobile money and card are what the providers owe the hotel until they settle to the bank.</p>
    </section>
  );
}

function Tax({ d }: { d: TaxSummary }) {
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h2">Taxes and levies</h2>
      </div>
      {d.rows.length === 0 ? (
        <Empty icon="inbox" title="No tax collected in this period" />
      ) : (
        <table className="ledger fin">
          <thead>
            <tr>
              <th>Tax</th>
              <th className="r">Collected</th>
              <th className="r">Returned</th>
              <th className="r">Net due</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.name}>
                <td>{r.name}</td>
                <td className="r num">{formatGhs(r.collected)}</td>
                <td className="r num">{formatGhs(r.returned)}</td>
                <td className="r num">{formatGhs(r.net)}</td>
              </tr>
            ))}
            <tr className="strong">
              <td>Total</td>
              <td />
              <td />
              <td className="r num">{formatGhs(d.totalNet)}</td>
            </tr>
          </tbody>
        </table>
      )}
      <p className="small muted" style={{ marginTop: 12 }}>Sales before tax in this period: <span className="num">{formatGhs(d.salesBeforeTax)}</span></p>
      <p className="hint">Taxes are listed by the name on the tax line. The rates and names are set with your accountant under Setup; this report is a working summary, not a filed return.</p>
    </section>
  );
}

function GL({ d }: { d: GeneralLedger }) {
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h2">{d.account.code} · {d.account.name}</h2>
      </div>
      <div className="table-scroll">
        <table className="ledger fin">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th className="r">Debit</th>
              <th className="r">Credit</th>
              <th className="r">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td />
              <td className="muted">Opening balance</td>
              <td />
              <td />
              <td className="r num">{formatGhs(d.opening)}</td>
            </tr>
            {d.rows.map((r, i) => (
              <tr key={i}>
                <td>{formatDate(r.date)}</td>
                <td>{r.memo}</td>
                <td className="r num">{r.debit ? formatGhs(r.debit) : ''}</td>
                <td className="r num">{r.credit ? formatGhs(r.credit) : ''}</td>
                <td className="r num">{formatGhs(r.balance)}</td>
              </tr>
            ))}
            <tr className="strong">
              <td />
              <td>Closing balance</td>
              <td className="r num">{formatGhs(d.totalDebit)}</td>
              <td className="r num">{formatGhs(d.totalCredit)}</td>
              <td className="r num">{formatGhs(d.closing)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

const METHOD: Record<string, string> = { CASH: 'Cash', MOMO: 'Mobile money', CARD: 'Card', BANK_TRANSFER: 'Bank transfer', ROOM: 'Credited to a room' };

function RefundList({ rows }: { rows: RefundRow[] }) {
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h2">Refunds</h2>
      </div>
      {rows.length === 0 ? (
        <Empty icon="inbox" title="No refunds yet" />
      ) : (
        <div className="list">
          {rows.map((r) => (
            <div key={r.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{r.label}</span>
                <span className="num">{formatGhs(r.amount)}</span>
              </div>
              <span className="small muted">
                {new Date(r.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {r.by} · {r.lines.map((l) => `${METHOD[l.method] ?? l.method}${l.reference && l.method !== 'ROOM' ? ` (${l.reference})` : ''}`).join(', ')}
              </span>
              <span className="hint">{r.reason}</span>
            </div>
          ))}
        </div>
      )}
      <p className="hint">Every refund needs a reason and is kept for good. Cash refunds come out of the manager’s till; mobile money and card refunds are made on the provider’s app or terminal and recorded here with their reference.</p>
    </section>
  );
}
